import "./selector.css";

// Icon selector — a row of small square icon-buttons forming an exclusive
// choice (radio group). Each cell is a native <button> so keyboard nav and
// focus rings work, with a canvas glyph drawn inside. Selected cell lifts
// slightly and shows a thin ice-blue underline plus a filled-glow icon.
//
// The most common application is the waveform picker (sine/tri/square/saw)
// on each oscillator. Filter mode, distortion curve, and LFO destination
// all use the same component with different glyphs.

/** Function that draws one selector glyph into a 2D context with origin at
 * the top-left of the cell. `size` is the cell's CSS dimension. */
export type GlyphFn = (ctx: CanvasRenderingContext2D, size: number) => void;

export interface SelectorOption<V extends string | number> {
  value: V;
  /** Drawing function for the glyph. */
  glyph: GlyphFn;
  /** Optional aria-label / tooltip. Defaults to the value. */
  label?: string;
}

export interface SelectorOptions<V extends string | number> {
  container: HTMLElement;
  options: SelectorOption<V>[];
  value?: V;
  onChange?: (value: V) => void;
  /** Forced visual state for the demo showcase. */
  forceSelected?: V;
}

const CELL_SIZE = 36;
const GLYPH_INSET = 8;

export class Selector<V extends string | number> {
  private root: HTMLDivElement;
  private cells: HTMLButtonElement[] = [];
  private canvases: HTMLCanvasElement[] = [];
  private opts: SelectorOption<V>[];
  private currentValue: V;
  private onChange: ((value: V) => void) | undefined;

  constructor(opts: SelectorOptions<V>) {
    if (opts.options.length === 0) {
      throw new Error("selector: at least one option required");
    }
    this.opts = opts.options;
    this.currentValue =
      opts.forceSelected ?? opts.value ?? opts.options[0]!.value;
    if (opts.onChange) this.onChange = opts.onChange;

    this.root = document.createElement("div");
    this.root.className = "selector";
    this.root.setAttribute("role", "radiogroup");

    for (const o of this.opts) {
      const cell = document.createElement("button");
      cell.type = "button";
      cell.className = "selector-cell";
      cell.setAttribute("role", "radio");
      cell.setAttribute("aria-label", o.label ?? String(o.value));

      const canvas = document.createElement("canvas");
      canvas.width = CELL_SIZE;
      canvas.height = CELL_SIZE;
      canvas.style.width = `${CELL_SIZE}px`;
      canvas.style.height = `${CELL_SIZE}px`;

      cell.append(canvas);
      this.root.append(cell);
      this.cells.push(cell);
      this.canvases.push(canvas);

      if (!opts.forceSelected) {
        cell.addEventListener("click", () => this.select(o.value));
      }
    }

    opts.container.append(this.root);
    this.refresh();
  }

  getValue(): V {
    return this.currentValue;
  }

  select(value: V): void {
    if (value === this.currentValue) return;
    this.currentValue = value;
    this.refresh();
    this.onChange?.(value);
  }

  private refresh(): void {
    for (let i = 0; i < this.opts.length; i++) {
      const o = this.opts[i]!;
      const cell = this.cells[i]!;
      const canvas = this.canvases[i]!;
      const isSelected = o.value === this.currentValue;
      cell.classList.toggle("is-selected", isSelected);
      cell.setAttribute("aria-checked", isSelected ? "true" : "false");
      drawGlyphCell(canvas, o.glyph, isSelected);
    }
  }
}

// ----- standard glyph library -----

function drawGlyphCell(
  canvas: HTMLCanvasElement,
  glyph: GlyphFn,
  selected: boolean,
): void {
  const dpr = window.devicePixelRatio || 1;
  const px = CELL_SIZE * dpr;
  if (canvas.width !== px) {
    canvas.width = px;
    canvas.height = px;
  }
  const ctx = canvas.getContext("2d");
  if (!ctx) return;
  ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  ctx.clearRect(0, 0, CELL_SIZE, CELL_SIZE);

  ctx.save();
  if (selected) {
    // Selected glyph glows ice-blue.
    ctx.shadowColor = "rgba(56, 189, 248, 0.85)";
    ctx.shadowBlur = 8;
    ctx.strokeStyle = "#7dd3fc";
    ctx.fillStyle = "rgba(125, 211, 252, 0.25)";
  } else {
    ctx.strokeStyle = "rgba(124, 135, 148, 0.85)";
    ctx.fillStyle = "transparent";
  }
  ctx.lineWidth = 1.5;
  ctx.lineCap = "round";
  ctx.lineJoin = "round";

  glyph(ctx, CELL_SIZE);
  ctx.restore();
}

/** Drawing helpers that produce a single-cycle line in the standard
 * selector cell. All glyphs assume `CELL_SIZE` cell width and inset by
 * `GLYPH_INSET` on each side. */

export const GLYPHS = {
  sine(ctx: CanvasRenderingContext2D, size: number): void {
    const inset = GLYPH_INSET;
    const y = size / 2;
    const w = size - 2 * inset;
    ctx.beginPath();
    for (let i = 0; i <= w; i++) {
      const x = inset + i;
      const phase = (i / w) * Math.PI * 2;
      const dy = Math.sin(phase) * (size / 2 - inset - 2);
      if (i === 0) ctx.moveTo(x, y - dy);
      else ctx.lineTo(x, y - dy);
    }
    ctx.stroke();
  },

  triangle(ctx: CanvasRenderingContext2D, size: number): void {
    const inset = GLYPH_INSET;
    const w = size - 2 * inset;
    const peak = size / 2 - inset - 2;
    const cy = size / 2;
    ctx.beginPath();
    ctx.moveTo(inset, cy);
    ctx.lineTo(inset + w * 0.25, cy - peak);
    ctx.lineTo(inset + w * 0.75, cy + peak);
    ctx.lineTo(inset + w, cy);
    ctx.stroke();
  },

  square(ctx: CanvasRenderingContext2D, size: number): void {
    const inset = GLYPH_INSET;
    const w = size - 2 * inset;
    const peak = size / 2 - inset - 2;
    const cy = size / 2;
    ctx.beginPath();
    ctx.moveTo(inset, cy);
    ctx.lineTo(inset, cy - peak);
    ctx.lineTo(inset + w * 0.5, cy - peak);
    ctx.lineTo(inset + w * 0.5, cy + peak);
    ctx.lineTo(inset + w, cy + peak);
    ctx.lineTo(inset + w, cy);
    ctx.stroke();
  },

  saw(ctx: CanvasRenderingContext2D, size: number): void {
    const inset = GLYPH_INSET;
    const w = size - 2 * inset;
    const peak = size / 2 - inset - 2;
    const cy = size / 2;
    ctx.beginPath();
    ctx.moveTo(inset, cy + peak);
    ctx.lineTo(inset + w, cy - peak);
    ctx.lineTo(inset + w, cy + peak);
    ctx.stroke();
  },

  /** Generic filter-mode glyphs: low-pass, high-pass, band-pass, notch.
   * Drawn as the approximate magnitude response. */
  filterLowpass(ctx: CanvasRenderingContext2D, size: number): void {
    const inset = GLYPH_INSET;
    const w = size - 2 * inset;
    const h = size - 2 * inset;
    const yTop = inset + 2;
    const yBot = inset + h - 2;
    ctx.beginPath();
    ctx.moveTo(inset, yTop);
    ctx.lineTo(inset + w * 0.45, yTop);
    ctx.quadraticCurveTo(inset + w * 0.7, yTop, inset + w, yBot);
    ctx.stroke();
  },
  filterHighpass(ctx: CanvasRenderingContext2D, size: number): void {
    const inset = GLYPH_INSET;
    const w = size - 2 * inset;
    const h = size - 2 * inset;
    const yTop = inset + 2;
    const yBot = inset + h - 2;
    ctx.beginPath();
    ctx.moveTo(inset, yBot);
    ctx.quadraticCurveTo(inset + w * 0.3, yTop, inset + w * 0.55, yTop);
    ctx.lineTo(inset + w, yTop);
    ctx.stroke();
  },
  filterBandpass(ctx: CanvasRenderingContext2D, size: number): void {
    const inset = GLYPH_INSET;
    const w = size - 2 * inset;
    const h = size - 2 * inset;
    const yTop = inset + 2;
    const yBot = inset + h - 2;
    const cx = inset + w / 2;
    ctx.beginPath();
    ctx.moveTo(inset, yBot);
    ctx.quadraticCurveTo(cx - w * 0.15, yBot, cx, yTop);
    ctx.quadraticCurveTo(cx + w * 0.15, yBot, inset + w, yBot);
    ctx.stroke();
  },
  filterNotch(ctx: CanvasRenderingContext2D, size: number): void {
    const inset = GLYPH_INSET;
    const w = size - 2 * inset;
    const h = size - 2 * inset;
    const yTop = inset + 2;
    const yBot = inset + h - 2;
    const cx = inset + w / 2;
    ctx.beginPath();
    ctx.moveTo(inset, yTop);
    ctx.quadraticCurveTo(cx - w * 0.15, yTop, cx, yBot);
    ctx.quadraticCurveTo(cx + w * 0.15, yTop, inset + w, yTop);
    ctx.stroke();
  },
};
